import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { useTranslation } from "react-i18next";

function parseJsonMaybe(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(String(raw));
  } catch {
    return null;
  }
}

function resolveMessageJson(message) {
  return (
    parseJsonMaybe(message?.json_whatsapp) ||
    parseJsonMaybe(message?.metadata) ||
    (message?.metadata && typeof message.metadata === "object" ? message.metadata : null) ||
    null
  );
}

export function resolveVideoCallSummary(message) {
  const json = resolveMessageJson(message);
  const summary = json?.xpro_video_call_summary;
  if (summary && typeof summary === "object") return summary;
  const code = json?.xpro_log?.code || message?.logMeta?.code;
  if (code === "video_call_summary") {
    return { plainOnly: true, text: message?.text || message?.mensagem || message?.content || "" };
  }
  return null;
}

export function resolveVideoCallRecording(message) {
  const json = resolveMessageJson(message);
  const rec = json?.xpro_video_recording;
  if (!rec || typeof rec !== "object") return null;
  const url = String(
    message?.video || message?.videoUrl || message?.url || rec.url || ""
  ).trim();
  if (!url) return null;
  return {
    url,
    durationSec: rec.durationSec != null ? Number(rec.durationSec) : null,
    durationLabel:
      rec.durationSec != null && Number(rec.durationSec) > 0
        ? `${Math.round(Number(rec.durationSec))}s`
        : null,
    roomId: rec.roomId != null ? Number(rec.roomId) : null,
  };
}

export function resolveVideoCallTranscript(message) {
  const json = resolveMessageJson(message);
  const tr = json?.xpro_video_call_transcript;
  if (!tr || typeof tr !== "object") return null;
  const lines = Array.isArray(tr.transcriptLines) ? tr.transcriptLines : [];
  const text = String(message?.text || message?.mensagem || message?.content || "").trim();
  if (!lines.length && !text) return null;
  return { lines, text, linesCount: tr.lines != null ? Number(tr.lines) : lines.length };
}

/** Artefatos soltos (transcrição/gravação) que devem sumir do chat de equipe. */
export function isStandaloneTeamVideoArtifact(message) {
  const json = resolveMessageJson(message);
  if (!json || typeof json !== "object") return false;
  // Cards pós-chamada devem ficar no balão do usuário que iniciou (como o convite).
  // Mensagens de sistema com resumo são legado/fallback e não devem aparecer.
  if (
    (message?.authorType === "system" || message?.messageType === "system") &&
    (json.xpro_video_call_summary ||
      json.xpro_video_call_transcript ||
      json.xpro_video_recording)
  ) {
    return true;
  }
  if (json.xpro_team_video_invite || json.xpro_video_call_summary) return false;
  if (json.xpro_video_call_transcript) return true;
  if (json.xpro_video_recording) return true;
  return false;
}

/**
 * Convite antigo que já tem card de resumo (mesmo roomId) — esconde para ficar 1 balão.
 */
export function isSupersededTeamVideoInvite(message, allMessages = []) {
  const json = resolveMessageJson(message);
  if (!json?.xpro_team_video_invite) return false;
  if (json.xpro_video_call_summary) return false;
  if (json.callSuperseded || json.callStatus === "ended") return true;

  const roomId = Number(json.roomId);
  const msgId = Number(message?.id);
  return (allMessages || []).some((other) => {
    if (!other || Number(other.id) === msgId) return false;
    const otherJson = resolveMessageJson(other);
    if (!otherJson?.xpro_video_call_summary) return false;
    const otherRoom = Number(
      otherJson.xpro_video_call_summary?.roomId ?? otherJson.roomId
    );
    if (Number.isFinite(roomId) && roomId > 0 && otherRoom === roomId) return true;
    // Sem roomId confiável: se já existe qualquer resumo posterior, esconde o convite aberto.
    if (!Number.isFinite(roomId) || roomId <= 0) {
      return Number(other.id) > msgId;
    }
    return false;
  });
}

export function shouldHideTeamVideoMessage(message, allMessages = []) {
  return (
    isStandaloneTeamVideoArtifact(message) ||
    isSupersededTeamVideoInvite(message, allMessages)
  );
}

function formatDurationLabel(summary) {
  if (summary?.durationLabel) return summary.durationLabel;
  if (summary?.durationSec != null && Number(summary.durationSec) > 0) {
    return `${Math.round(Number(summary.durationSec))}s`;
  }
  return null;
}

function DetailToggle({ open, onToggle, labelOpen, labelClosed, onAccentBubble = false }) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  return (
    <Button
      size="small"
      fullWidth
      onClick={onToggle}
      endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      sx={{
        justifyContent: "space-between",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.8125rem",
        minHeight: 34,
        px: 1.1,
        ...(onAccentBubble
          ? {
              color: "inherit",
              border: `1px solid ${alpha("#ffffff", 0.45)}`,
              bgcolor: alpha("#ffffff", 0.12),
              "&:hover": {
                borderColor: alpha("#ffffff", 0.8),
                bgcolor: alpha("#ffffff", 0.2),
              },
            }
          : {
              color: "text.primary",
              border: `1px solid ${alpha(accent, 0.28)}`,
              bgcolor: alpha(accent, 0.08),
              "&:hover": {
                borderColor: accent,
                bgcolor: alpha(accent, 0.14),
              },
            }),
      }}
    >
      {open ? labelOpen : labelClosed}
    </Button>
  );
}

/**
 * Conteúdo pós-chamada.
 * - embedded: dentro do balão de equipe (mesmo padrão do convite)
 * - default: card independente (timeline de atendimento)
 */
const VideoCallSummaryMessage = ({
  message,
  recordingUrlOverride = null,
  variant = "default",
  embedded = false,
  onAccentBubble = false,
  bubbleFg,
}) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const [videoError, setVideoError] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [recordingOpen, setRecordingOpen] = useState(false);

  const json = resolveMessageJson(message);
  const isTeam =
    variant === "team" ||
    Boolean(json?.xpro_team_video_invite) ||
    json?.callContext === "team";

  const summary = resolveVideoCallSummary(message);
  const recordingOnly = !summary ? resolveVideoCallRecording(message) : null;
  const transcriptOnly = !summary ? resolveVideoCallTranscript(message) : null;

  const recordingUrl =
    recordingUrlOverride ||
    summary?.recordingUrl ||
    recordingOnly?.url ||
    null;

  const transcriptLines = useMemo(() => {
    if (Array.isArray(summary?.transcriptLines) && summary.transcriptLines.length) {
      return summary.transcriptLines;
    }
    if (transcriptOnly?.lines?.length) return transcriptOnly.lines;
    return [];
  }, [summary, transcriptOnly]);

  const transcriptText = useMemo(() => {
    const fromSummary = String(summary?.transcriptText || "").trim();
    if (fromSummary) return fromSummary;
    const fromOnly = String(transcriptOnly?.text || "").trim();
    if (fromOnly) return fromOnly;
    const raw = String(message?.content || message?.mensagem || message?.text || "").trim();
    if (/^Transcrição da chamada/i.test(raw)) return raw;
    return "";
  }, [summary, transcriptOnly, message]);

  const hasTranscriptContent = transcriptLines.length > 0 || Boolean(transcriptText);
  const hasTranscript = hasTranscriptContent || Boolean(summary?.hasTranscript);

  const aiSummary = String(summary?.aiSummary || "").trim();
  const keyPoints = Array.isArray(summary?.keyPoints)
    ? summary.keyPoints.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  const actionItems = Array.isArray(summary?.actionItems)
    ? summary.actionItems.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  const hasAiBlock = Boolean(aiSummary || keyPoints.length || actionItems.length);

  useEffect(() => {
    setVideoError(false);
  }, [recordingUrl]);

  if (!summary && !recordingOnly && !transcriptOnly) return null;

  if (summary?.plainOnly && !recordingUrl && !hasTranscript && !hasAiBlock) {
    return (
      <Typography
        variant="body2"
        sx={{
          whiteSpace: "pre-wrap",
          color: embedded ? "inherit" : "text.secondary",
          opacity: embedded ? 0.92 : 1,
        }}
      >
        {summary.text}
      </Typography>
    );
  }

  const durationLabel = summary
    ? formatDurationLabel(summary)
    : recordingOnly?.durationLabel;
  const participants = Array.isArray(summary?.participants) ? summary.participants : [];
  const endReason = summary?.endReasonLabel || null;
  const title = isTeam
    ? t("video.teamCallEndedTitle", "Chamada da equipe")
    : t("video.summaryTitle", "Chamada de vídeo");

  const statusBits = [
    durationLabel,
    endReason,
    participants.length === 1
      ? t("video.participantCountOne", "1 participante")
      : participants.length > 1
        ? t("video.participantCountMany", "{{count}} participantes", {
            count: participants.length,
          })
        : null,
  ].filter(Boolean);

  const mutedSx = embedded
    ? { color: "inherit", opacity: onAccentBubble ? 0.88 : 0.75 }
    : { color: "text.secondary" };
  const strongSx = embedded
    ? { color: bubbleFg || "inherit" }
    : { color: "text.primary" };
  const dividerColor = onAccentBubble
    ? alpha("#ffffff", 0.28)
    : alpha(theme.palette.divider, 0.7);
  const panelBg = onAccentBubble
    ? alpha("#ffffff", 0.12)
    : theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.03)
      : alpha(theme.palette.grey[500], 0.06);

  const body = (
    <>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
        <VideocamOutlinedIcon sx={{ fontSize: 17, opacity: 0.9, mt: 0.15, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, lineHeight: 1.3, ...strongSx }}
          >
            {title}
          </Typography>
          {statusBits.length > 0 ? (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.2, lineHeight: 1.4, ...mutedSx }}
            >
              {statusBits.join(" · ")}
            </Typography>
          ) : null}
          {participants.length > 0 ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.35,
                lineHeight: 1.4,
                ...mutedSx,
                opacity: embedded ? 0.75 : undefined,
              }}
            >
              {participants.map((p) => p.name).filter(Boolean).join(", ")}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ mt: 1.1, pt: 1, borderTop: `1px solid ${dividerColor}` }}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.45 }}>
          <AutoAwesomeOutlinedIcon
            sx={{
              fontSize: 14,
              color: onAccentBubble ? "inherit" : "primary.main",
              opacity: onAccentBubble ? 0.95 : 1,
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 700, ...strongSx }}>
            {t("video.aiSummaryTitle", "Resumo")}
          </Typography>
        </Stack>

        {hasAiBlock ? (
          <>
            {aiSummary ? (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8125rem",
                  lineHeight: 1.45,
                  mb: keyPoints.length || actionItems.length ? 0.75 : 0,
                  ...mutedSx,
                }}
              >
                {aiSummary}
              </Typography>
            ) : null}
            {keyPoints.length > 0 ? (
              <Box sx={{ mb: actionItems.length ? 0.75 : 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, ...mutedSx }}>
                  {t("video.keyPointsTitle", "Pontos principais")}
                </Typography>
                <List dense disablePadding sx={{ mt: 0.15 }}>
                  {keyPoints.map((item, idx) => (
                    <ListItem key={`kp-${idx}`} disableGutters sx={{ py: 0.1, alignItems: "flex-start" }}>
                      <Typography
                        component="span"
                        sx={{ mr: 0.75, fontSize: "0.75rem", lineHeight: 1.5, ...mutedSx }}
                      >
                        •
                      </Typography>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          variant: "caption",
                          sx: { lineHeight: 1.4, ...mutedSx },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}
            {actionItems.length > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, ...mutedSx }}>
                  {t("video.actionItemsTitle", "Próximos passos")}
                </Typography>
                <List dense disablePadding sx={{ mt: 0.15 }}>
                  {actionItems.map((item, idx) => (
                    <ListItem key={`ai-${idx}`} disableGutters sx={{ py: 0.1, alignItems: "flex-start" }}>
                      <Typography
                        component="span"
                        sx={{ mr: 0.75, fontSize: "0.75rem", lineHeight: 1.5, ...mutedSx }}
                      >
                        •
                      </Typography>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          variant: "caption",
                          sx: { lineHeight: 1.4, ...mutedSx },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem", lineHeight: 1.45, ...mutedSx }}>
            {hasTranscriptContent
              ? t(
                  "video.aiSummaryUnavailable",
                  "Resumo automático indisponível. Confira a transcrição abaixo."
                )
              : t(
                  "video.aiSummaryEmpty",
                  "Não houve falas suficientes para gerar um resumo automático."
                )}
          </Typography>
        )}
      </Box>

      {(recordingUrl || hasTranscriptContent) ? (
        <Stack spacing={0.75} sx={{ mt: 1.15 }}>
          {recordingUrl ? (
            <Box>
              <DetailToggle
                open={recordingOpen}
                onToggle={() => setRecordingOpen((v) => !v)}
                labelClosed={t("video.showRecording", "Ver gravação")}
                labelOpen={t("video.hideRecording", "Ocultar gravação")}
                onAccentBubble={onAccentBubble}
              />
              <Collapse in={recordingOpen}>
                <Box
                  sx={{
                    mt: 0.75,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    bgcolor: "#0f1115",
                    border: onAccentBubble
                      ? `1px solid ${alpha("#ffffff", 0.2)}`
                      : `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  }}
                >
                  {videoError ? (
                    <Box
                      sx={{
                        py: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.75,
                        color: "grey.500",
                      }}
                    >
                      <VideocamOffIcon sx={{ fontSize: 36 }} />
                      <Typography variant="caption">
                        {t(
                          "video.summaryRecordingUnavailable",
                          "Não foi possível carregar a gravação"
                        )}
                      </Typography>
                    </Box>
                  ) : (
                    <video
                      controls
                      preload="metadata"
                      style={{
                        display: "block",
                        width: "100%",
                        maxHeight: 240,
                        background: "#0f1115",
                      }}
                      onError={() => setVideoError(true)}
                    >
                      <source src={recordingUrl} type="video/mp4" />
                    </video>
                  )}
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      display: "flex",
                      justifyContent: "flex-end",
                      bgcolor: alpha(theme.palette.common.white, 0.04),
                    }}
                  >
                    <Button
                      size="small"
                      href={recordingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: "13px !important" }} />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        minHeight: 28,
                        px: 1,
                        color: onAccentBubble ? "#fff" : undefined,
                      }}
                    >
                      {t("video.summaryOpenRecording", "Abrir em nova aba")}
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          ) : null}

          {hasTranscriptContent ? (
            <Box>
              <DetailToggle
                open={transcriptOpen}
                onToggle={() => setTranscriptOpen((v) => !v)}
                labelClosed={t("video.showTranscript", "Ver transcrição")}
                labelOpen={t("video.hideTranscript", "Ocultar transcrição")}
                onAccentBubble={onAccentBubble}
              />
              <Collapse in={transcriptOpen}>
                <Box
                  sx={{
                    mt: 0.75,
                    maxHeight: 240,
                    overflow: "auto",
                    borderRadius: 1.5,
                    bgcolor: panelBg,
                    px: 1.25,
                    py: 1,
                  }}
                >
                  {transcriptLines.length > 0 ? (
                    transcriptLines.map((line, idx) => (
                      <Typography
                        key={`tl-${idx}`}
                        variant="caption"
                        component="div"
                        sx={{ lineHeight: 1.45, mb: 0.45, ...mutedSx }}
                      >
                        <Box component="span" sx={{ fontWeight: 700, ...strongSx }}>
                          {line.who || t("video.participantFallback", "Participante")}
                        </Box>
                        {`: ${line.text}`}
                      </Typography>
                    ))
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{ whiteSpace: "pre-wrap", display: "block", ...mutedSx }}
                    >
                      {transcriptText}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          ) : null}
        </Stack>
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          color: bubbleFg || "inherit",
          maxWidth: 320,
          minWidth: 0,
          width: "100%",
        }}
      >
        {body}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 400,
        textAlign: "left",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.9),
        bgcolor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.04)
            : theme.palette.background.paper,
        overflow: "hidden",
        boxShadow:
          theme.palette.mode === "dark" ? "none" : `0 1px 2px ${alpha("#000", 0.04)}`,
        px: 1.5,
        pt: 1.25,
        pb: 1.25,
      }}
    >
      {body}
    </Box>
  );
};

export default VideoCallSummaryMessage;
